/**
 * SEO Component for Meta Tags and Canonical URLs
 * Uses react-helmet-async for dynamic meta tag management
 * 
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Created: October 27, 2025
 * Updated: Jun 12, 2026 — version meta follows package.json (currently 2.14.0)
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import packageJson from '../../package.json';
import { useLanguage } from '../context/LanguageContext';

const SEO = ({
  title = 'Cranford Publications — Celtic Fiddle Music Shop',
  description = 'Cranford Publications — Celtic fiddle music shop: Irish, Scottish, and Cape Breton tune books, CDs, ABC notation, and learning materials. Secure checkout (Stripe & PayPal).',
  canonical = null,
  image = '/images/collage9.jpg',
  type = 'website',
  keywords = 'celtic music, fiddle music, Cape Breton, Irish music, Scottish music, traditional music, music books, fiddle books, ABC notation, Cranford Publications',
  robots = 'index, follow',
  jsonLd = null
}) => {
  const { meta } = useLanguage();
  // Build canonical URL - default to current location if not provided
  // IMPORTANT: Strip query parameters from canonical URLs to avoid duplicate content issues
  // Always use www.cranfordpub.ca for consistency (prevents non-www duplicate content)
  const baseUrl = 'https://www.cranfordpub.ca';
  let canonicalUrl;

  if (canonical) {
    // If canonical is provided, use it (already absolute or relative)
    canonicalUrl = canonical.startsWith('http') ? canonical : `${baseUrl}${canonical}`;
  } else {
    // Use current pathname but strip query parameters and hash
    const pathname = (typeof window !== 'undefined' ? window.location.pathname : '/') || '/';
    canonicalUrl = `${baseUrl}${pathname}`;
  }

  // Ensure no query parameters or hash in canonical URL
  canonicalUrl = canonicalUrl.split('?')[0].split('#')[0];

  // Ensure image is absolute URL
  const imageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="Cranford Publications" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Additional SEO */}
      <meta name="robots" content={robots} />
      <html lang={meta.htmlLang} />
      <meta name="language" content={meta.nativeName} />
      <meta name="author" content="Cranford Publications" />
      <meta name="version" content={packageJson.version} />
      <meta property="og:locale" content={meta.ogLocale} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

